import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Icon, Message } from 'semantic-ui-react';
import { PageHeader, PageSection, H2, PageContent } from 'common/styles/page';
import { Table, TableHeader, TableRow, TableBody, TableCell, HeaderCell } from 'common/components/Table';
import styled from 'styled-components';
import ImportMemberRecordModal from './ImportMemberRecordModal';
import { StyledAddButton, StyledCancelButton } from 'common/components/button/StyledButton';

type ImportRow = {
    row_number: number;
    status?: 'created' | 'updated' | 'skipped' | 'error' | string;
    message?: string;
    data?: Record<string, any>;
};

type ImportResult = {
    processed?: number;
    created?: number;
    updated?: number;
    skipped?: number;
    errors?: any[];
    rows?: ImportRow[];
};

const ImportMemberResult: React.FC = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const result: ImportResult | undefined = state?.result || state || undefined;
    const [reimportOpen, setReimportOpen] = useState(false);

    if (!result) {
        return (
            <PageSection>
                <PageHeader
                    title="Member Record Import Results"
                />
                <p>No member record import results.</p>
            </PageSection>
        );
    }

    const rows = result.rows || [];
    const topErrors = result.errors || [];

    return (
        <PageContent>
        <PageSection>
            <PageHeader
                title="Member Record Import Results"
                controls={
                    <>
                        <StyledAddButton width="150" label='Reimport Data' onClick={() => setReimportOpen(true)} />
                        <StyledCancelButton label='Close' onClick={() => navigate('/app/team-management')} />
                    </>
                }
                divider
            />  

            <Summary>
                <SummaryItem>
                    <Stat>{result.processed ?? 0}</Stat>
                    <Label>Processed</Label>
                </SummaryItem>
                <SummaryItem>
                    <Stat>{result.created ?? 0}</Stat>
                    <Label>Created</Label>
                </SummaryItem>
                <SummaryItem>
                    <Stat>{result.updated ?? 0}</Stat>
                    <Label>Updated</Label>
                </SummaryItem>
                <SummaryItem>
                    <Stat>{result.skipped ?? 0}</Stat>
                    <Label>Skipped</Label>
                </SummaryItem>
            </Summary>

            {topErrors.length > 0 && (
                <Message negative>
                    <Message.Header>Import Errors</Message.Header>
                    <p>{String(topErrors)}</p>
                </Message>
            )}

            <H2 padtop>Row Details</H2>
            <Table celled compact>
                <TableHeader>
                    <TableRow>
                        <HeaderCell width={1}>Row</HeaderCell>
                        <HeaderCell width={2}>First Name</HeaderCell>
                        <HeaderCell width={2}>Last Name</HeaderCell>
                        <HeaderCell width={3}>Email</HeaderCell>
                        <HeaderCell width={2}>Status</HeaderCell>
                        <HeaderCell width={4}>Message</HeaderCell>
                        <HeaderCell>Row Data</HeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((r, idx) => {
                        const data = r.data || {};
                        const firstName = data.first_name ?? data.firstName ?? '';
                        const lastName = data.last_name ?? data.lastName ?? '';
                        const email = data.email ?? data.Email ?? '';

                        const excludedKeys = new Set([
                            'first_name',
                            'firstName',
                            'last_name',
                            'lastName',
                            'email',
                            'Email',
                            'assessment_period',
                            'assessmentPeriod',
                        ]);

                        const remaining = Object.entries(data).filter(([k]) => !excludedKeys.has(k));

                        return (
                            <TableRow key={idx} warning={r.status === 'skipped'} negative={r.status === 'error'}>
                                <TableCell>{r.row_number}</TableCell>
                                <TableCell>{firstName}</TableCell>
                                <TableCell>{lastName}</TableCell>
                                <TableCell>{email}</TableCell>
                                <TableCell>{renderStatus(r.status)}</TableCell>
                                <TableCell>{r.message || ''}</TableCell>
                                <TableCell>
                                    {remaining.length > 0 ? (
                                        <KeyValueList>
                                            {remaining.map(([k, v]) => (
                                                <KeyValue key={k} title={String(v === null || v === undefined ? '' : v)}>
                                                    <Key>{k}</Key>: <Value>{String(v)}</Value>
                                                </KeyValue>
                                            ))}
                                        </KeyValueList>
                                    ) : (
                                        '—'
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            <ImportMemberRecordModal open={reimportOpen} onClose={() => setReimportOpen(false)} />
        </PageSection>
    </PageContent>
    );
};

function renderStatus(status?: string) {
    switch (status) {
        case 'created':
            return <StatusCreated>Created</StatusCreated>;
        case 'updated':
            return <StatusUpdated>Updated</StatusUpdated>;
        case 'skipped':
            return <StatusSkipped>Skipped</StatusSkipped>;
        case 'error':
            return <StatusError>Error</StatusError>;
        default:
            return status || '—';
    }
}

const Summary = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
`;
const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100px;
  background: #fafafa;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #eee;
`;
const Stat = styled.div`
  font-weight: 700;
  font-size: 20px;
`;
const Label = styled.div`
  font-size: 12px;
  color: #666;
`;

const KeyValueList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
`;
const KeyValue = styled.div`
  font-size: 12px;
  color: #444;
`;
const Key = styled.span`
  font-weight: 600;
  color: #333;
`;
const Value = styled.span`
  color: #666;
`;

const StatusCreated = styled.span`
  color: #2e7d32;
  font-weight: 700;
`;
const StatusUpdated = styled.span`
  color: #1976d2;
  font-weight: 700;
`;
const StatusSkipped = styled.span`
  color: #ef6c00;
  font-weight: 700;
`;
const StatusError = styled.span`
  color: #d32f2f;
  font-weight: 700;
`;

export default ImportMemberResult;
